import * as React from 'react';
import { DocViewsModel, PerDocViewsModelProps } from '../../interfaces';
import { useQuery } from '@apollo/client/react';
import { GET_DOCUMENT_VIEWS_PER_ORG } from '../reports.graphql';
import { Box, Typography, CircularProgress, TextField } from '@mui/material';
import { InsertDriveFile } from '@mui/icons-material';
import { GeneralExcelExport, GeneralExcelExportProps } from '../brreg';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid/locales';

function ViewsPerDoc(props: PerDocViewsModelProps) {
  const { orgNumber, fromDate, toDate, productType } = props;

  const { loading, error, data } = useQuery<{ docViews: DocViewsModel[] }>(GET_DOCUMENT_VIEWS_PER_ORG, {
    variables: {
      orgNumber: orgNumber,
      fromDate: fromDate,
      toDate: toDate,
    },
  });

  const [searchQuery, setSearchQuery] = React.useState('');

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  if (loading) return <CircularProgress />;
  if (error) return <p>Error :(</p>;

  const filterDictionary = {
    bfs: (docView: DocViewsModel) => docView.sourceId === 1,
    bvn: (docView: DocViewsModel) => docView.sourceId === 3,
    bfsp: (docView: DocViewsModel) => docView.seriesId === 1,
    bfsb: (docView: DocViewsModel) => docView.seriesId === 2,
    bfsf: (docView: DocViewsModel) => docView.seriesId === 3,
  };

  let filteredData = data?.docViews;

  //filter data on product type
  if (filteredData) {
    const filterFn = filterDictionary[productType!];
    if (filterFn) {
      filteredData = filteredData.filter(filterFn);
    }
  }
  // filter data on search keyword
  if (filteredData) {
    filteredData = filteredData.filter((docView: DocViewsModel) => {
      const query = searchQuery.toLowerCase();
      return docView.title.toLowerCase().includes(query) || docView.views.toString().includes(query);
    });
  }

  const arrHeading: string[] = ['Tittel', 'Antall'];
  const dataSet = filteredData && filteredData.map((x) => [x.docNumber + ' ' + x.title, x.views]);
  const workBook = 'SIDEVISNING_ANTALL_PER_DOKUMENT';
  const fileName = 'sidevisning_per_dokument.xlsx';
  const propsExcel: GeneralExcelExportProps = {
    dataSet: JSON.stringify(dataSet),
    headings: JSON.stringify(arrHeading),
    workBookName: workBook,
    fileName: fileName,
  };

  const handleDocClick = (docId) => {
    const url = `https://www.byggforsk.no/dokument/${docId}`;
    window.open(url, '_blank');
  };

  const rows = (filteredData ?? []).map((docView: DocViewsModel) => ({
    id: docView.docId,
    docId: docView.docId,
    title: `${docView.docNumber} ${docView.title}`,
    views: docView.views,
  }));

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      field: 'title',
      headerName: 'Tittel',
      minWidth: 160,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <InsertDriveFile style={{ marginRight: '0.5em', color: '#555555' }} />
          <Typography
            sx={{ cursor: 'pointer' }}
            color="primary"
            onClick={() => handleDocClick(params.row.docId)}
          >
            {params.row.title}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'views',
      headerName: 'Antall',
      minWidth: 160,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      type: 'number',
    },
  ];

  return (
    <Box sx={{m: 2, p: 2, border: 1, borderColor: 'grey.300', borderRadius: 4}}>
      <Box
        sx={{
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <TextField
          label="Søk"
          value={searchQuery}
          onChange={handleSearch}
          sx={{
            flexGrow: 1,
            marginLeft: '16px',
          }}
        />
        {filteredData && <GeneralExcelExport {...propsExcel} />}
      </Box>
      <DataGridPro
        autoHeight
        density="compact"
        rows={rows}
        columns={columns}
        localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
        pagination
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
        sx={{ '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 } }}
      />
    </Box>
  );
}

export default ViewsPerDoc;
